import { StandardEvents } from '@shopify/events';

const FORCED_AVAILABILITY_FILTER = 'filter.v.availability';
const FORCED_AVAILABILITY_VALUE = '1';
const FALLBACK_IMAGE_CACHE_PREFIX = 'real:sale-category-image:v1:';
const MAX_FALLBACK_IMAGE_HYDRATIONS = 2;

/**
 * Turns the Sale collection's category cards into AJAX-powered filter shortcuts.
 * The cards remain real links so filtering still works if JavaScript is unavailable.
 */
class SaleCategoryTabs extends HTMLElement {
  /** @type {HTMLAnchorElement[]} */
  #links = [];
  #busy = false;
  /** @type {IntersectionObserver | null} */
  #imageObserver = null;
  /** @type {HTMLAnchorElement[]} */
  #imageHydrationQueue = [];
  #activeImageHydrations = 0;

  connectedCallback() {
    this.#links = Array.from(this.querySelectorAll('.group-block__link')).filter(
      (link) => link instanceof HTMLAnchorElement
    );

    for (const link of this.#links) link.addEventListener('click', this.#onLinkClick);

    document.addEventListener(StandardEvents.collectionUpdate, this.#onCollectionUpdate);
    window.addEventListener('popstate', this.#onPopState);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#syncSelectedCard(undefined, 'auto');
    this.#syncCategoryCounts();
    this.#initFallbackImages();
  }

  disconnectedCallback() {
    for (const link of this.#links) link.removeEventListener('click', this.#onLinkClick);

    document.removeEventListener(StandardEvents.collectionUpdate, this.#onCollectionUpdate);
    window.removeEventListener('popstate', this.#onPopState);
    window.removeEventListener('pageshow', this.#onPageShow);
    this.#imageObserver?.disconnect();
    this.#imageObserver = null;
    this.#imageHydrationQueue = [];
  }

  #initFallbackImages() {
    const cards = this.#links
      .map((link) => link.closest('.group-block'))
      .filter((card) => card instanceof HTMLElement && card.querySelector('.image-block .placeholder-image'));

    if (cards.length === 0) return;

    if (!('IntersectionObserver' in window)) {
      for (const card of cards) {
        const link = card.querySelector(':scope > .group-block__link');
        if (link instanceof HTMLAnchorElement) this.#queueFallbackImage(link);
      }
      return;
    }

    this.#imageObserver = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          observer.unobserve(entry.target);
          const link = entry.target.querySelector(':scope > .group-block__link');
          if (link instanceof HTMLAnchorElement) this.#queueFallbackImage(link);
        }
      },
      { rootMargin: '320px' }
    );

    for (const card of cards) this.#imageObserver.observe(card);
  }

  /** @param {HTMLAnchorElement} link */
  #queueFallbackImage(link) {
    const imageBlock = link.closest('.group-block')?.querySelector('.image-block');
    if (!(imageBlock instanceof HTMLElement) || !imageBlock.querySelector('.placeholder-image')) return;
    if (imageBlock.dataset.fallbackImageState) return;

    imageBlock.dataset.fallbackImageState = 'queued';
    this.#imageHydrationQueue.push(link);
    this.#drainFallbackImageQueue();
  }

  #drainFallbackImageQueue() {
    while (
      this.#activeImageHydrations < MAX_FALLBACK_IMAGE_HYDRATIONS &&
      this.#imageHydrationQueue.length > 0
    ) {
      const link = this.#imageHydrationQueue.shift();
      if (!(link instanceof HTMLAnchorElement)) continue;

      this.#activeImageHydrations += 1;
      void this.#hydrateFallbackImage(link).finally(() => {
        this.#activeImageHydrations -= 1;
        this.#drainFallbackImageQueue();
      });
    }
  }

  async #waitForImageParseOpportunity() {
    while (this.#busy && this.isConnected) {
      await new Promise((resolve) => window.setTimeout(resolve, 50));
    }

    if (!this.isConnected) return;

    await new Promise((resolve) => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => resolve(), { timeout: 250 });
      } else {
        window.requestAnimationFrame(() => resolve());
      }
    });

    if (this.#busy) await this.#waitForImageParseOpportunity();
  }

  /** @param {HTMLAnchorElement} link */
  async #hydrateFallbackImage(link) {
    const imageBlock = link.closest('.group-block')?.querySelector('.image-block');
    if (!(imageBlock instanceof HTMLElement) || !imageBlock.querySelector('.placeholder-image')) return;
    if (imageBlock.dataset.fallbackImageState && imageBlock.dataset.fallbackImageState !== 'queued') return;

    const categoryParam = this.dataset.categoryParam;
    const resultsSectionId = document.querySelector('results-list[section-id]')?.getAttribute('section-id');
    if (!categoryParam || !resultsSectionId) return;

    const filterUrl = new URL(link.href, window.location.href);
    const categoryValue = filterUrl.searchParams.get(categoryParam);
    if (!categoryValue) return;

    const cacheKey = `${FALLBACK_IMAGE_CACHE_PREFIX}${categoryValue}`;
    const cachedImage = this.#readFallbackImageCache(cacheKey);
    if (cachedImage !== undefined) {
      imageBlock.dataset.fallbackImageState = cachedImage ? 'loading' : 'placeholder';
      if (cachedImage) this.#renderFallbackImage(imageBlock, cachedImage);
      return;
    }

    imageBlock.dataset.fallbackImageState = 'loading';
    filterUrl.searchParams.set('section_id', resultsSectionId);
    filterUrl.searchParams.delete('page');

    try {
      const response = await fetch(filterUrl.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`Unable to load Sale category image (${response.status})`);

      const responseText = await response.text();
      await this.#waitForImageParseOpportunity();
      if (!this.isConnected) return;

      const documentFragment = new DOMParser().parseFromString(responseText, 'text/html');
      const sourceImage = documentFragment.querySelector(
        '.product-grid__item[data-product-id] img[src], .product-grid__item[data-product-id] img[srcset]'
      );
      const fallbackImage = sourceImage
        ? {
            src: sourceImage.getAttribute('src') || '',
            srcset: sourceImage.getAttribute('srcset') || '',
            width: sourceImage.getAttribute('width') || '',
            height: sourceImage.getAttribute('height') || '',
          }
        : null;

      this.#writeFallbackImageCache(cacheKey, fallbackImage);
      imageBlock.dataset.fallbackImageState = fallbackImage ? 'loading' : 'placeholder';
      if (fallbackImage) this.#renderFallbackImage(imageBlock, fallbackImage);
    } catch (_error) {
      imageBlock.dataset.fallbackImageState = 'placeholder';
    }
  }

  /** @param {string} cacheKey */
  #readFallbackImageCache(cacheKey) {
    try {
      const cachedValue = window.sessionStorage.getItem(cacheKey);
      return cachedValue === null ? undefined : JSON.parse(cachedValue);
    } catch (_error) {
      return undefined;
    }
  }

  /**
   * @param {string} cacheKey
   * @param {object | null} fallbackImage
   */
  #writeFallbackImageCache(cacheKey, fallbackImage) {
    try {
      window.sessionStorage.setItem(cacheKey, JSON.stringify(fallbackImage));
    } catch (_error) {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
  }

  /**
   * @param {HTMLElement} imageBlock
   * @param {{ src: string, srcset: string, width: string, height: string }} imageData
   */
  #renderFallbackImage(imageBlock, imageData) {
    const placeholder = imageBlock.querySelector('.placeholder-image');
    if (!(placeholder instanceof HTMLElement) || imageBlock.querySelector('.image-block__image')) return;

    const image = document.createElement('img');
    image.className = 'image-block__image border-style';
    image.alt = '';
    image.loading = 'eager';
    image.decoding = 'async';
    image.sizes = '(max-width: 749px) 44vw, 16vw';
    image.dataset.saleCategoryFallbackImage = '';

    if (imageData.width) image.width = Number(imageData.width);
    if (imageData.height) image.height = Number(imageData.height);

    const placeholderStyle = placeholder.getAttribute('style');
    if (placeholderStyle) image.setAttribute('style', placeholderStyle);

    image.addEventListener(
      'load',
      () => {
        if (!placeholder.isConnected || imageBlock.querySelector('.image-block__image')) return;
        placeholder.replaceWith(image);
        imageBlock.dataset.fallbackImageState = 'loaded';
      },
      { once: true }
    );
    image.addEventListener(
      'error',
      () => {
        imageBlock.dataset.fallbackImageState = 'placeholder';
      },
      { once: true }
    );

    if (imageData.srcset) image.srcset = imageData.srcset;
    if (imageData.src) image.src = imageData.src;
  }

  /** @returns {HTMLElement | null} */
  #getFacetsForm() {
    const collectionId = this.dataset.collectionId;
    if (!collectionId) return null;

    return document.querySelector(
      `facets-form-component[form-style][data-collection-id="${CSS.escape(collectionId)}"]`
    );
  }

  /**
   * @param {URL} [url]
   * @param {ScrollBehavior} [scrollBehavior]
   */
  #syncSelectedCard(url = new URL(window.location.href), scrollBehavior = 'smooth') {
    const categoryParam = this.dataset.categoryParam;
    if (!categoryParam) return;

    const activeCategoryValues = url.searchParams.getAll(categoryParam);
    let selectedLink = null;

    for (const link of this.#links) {
      const linkUrl = new URL(link.href, window.location.href);
      const linkCategoryValue = linkUrl.searchParams.get(categoryParam);
      const isSelected = activeCategoryValues.length === 1 && activeCategoryValues[0] === linkCategoryValue;

      if (isSelected) {
        link.setAttribute('aria-current', 'true');
        selectedLink = link;
      } else {
        link.removeAttribute('aria-current');
      }
    }

    this.#revealSelectedCard(selectedLink, scrollBehavior);
  }

  #syncCategoryCounts() {
    const categoryParam = this.dataset.categoryParam;
    if (!categoryParam) return;

    const facetInputs = Array.from(
      document.querySelectorAll(`input[name="${CSS.escape(categoryParam)}"]`)
    ).filter((input) => input instanceof HTMLInputElement);

    for (const link of this.#links) {
      const card = link.closest('.group-block');
      if (!(card instanceof HTMLElement)) continue;

      const categoryValue = new URL(link.href, window.location.href).searchParams.get(categoryParam);
      const facetInput = facetInputs.find((input) => input.value === categoryValue);
      const describedBy = facetInput?.getAttribute('aria-describedby')?.split(/\s+/).filter(Boolean) ?? [];
      const countElement = describedBy.map((id) => document.getElementById(id)).find(Boolean);
      const countText = countElement?.querySelector('[aria-hidden="true"]')?.textContent ?? countElement?.textContent;
      const count = Number.parseInt(countText?.replace(/[^0-9]/g, '') ?? '', 10);
      let countPill = card.querySelector(':scope > .sale-category-tabs__count');

      if (!Number.isFinite(count)) {
        countPill?.remove();
        continue;
      }

      if (!(countPill instanceof HTMLElement)) {
        countPill = document.createElement('span');
        countPill.className = 'sale-category-tabs__count';
        countPill.setAttribute('aria-hidden', 'true');
        card.append(countPill);
      }

      countPill.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en').format(count);
    }
  }

  /**
   * @param {HTMLAnchorElement | null} selectedLink
   * @param {ScrollBehavior} scrollBehavior
   */
  #revealSelectedCard(selectedLink, scrollBehavior) {
    if (!selectedLink || !window.matchMedia('(max-width: 749px)').matches) return;

    const card = selectedLink.closest('.group-block');
    const scroller = card?.parentElement;
    if (!card || !scroller?.matches('[data-mobile-horizontal-scroll]')) return;

    window.requestAnimationFrame(() => {
      const cardBounds = card.getBoundingClientRect();
      const scrollerBounds = scroller.getBoundingClientRect();
      const hasContentBefore = scroller.scrollLeft > 2;
      const remainingScroll = scroller.scrollWidth - scroller.clientWidth - scroller.scrollLeft;
      const hasContentAfter = remainingScroll > 2;
      const clearLeftEdge = scrollerBounds.left + (hasContentBefore ? 16 : 0);
      const clearRightEdge = scrollerBounds.right - (hasContentAfter ? 32 : 0);
      const isClearOfScrollCues = cardBounds.left >= clearLeftEdge && cardBounds.right <= clearRightEdge;
      if (isClearOfScrollCues) return;

      const targetLeft =
        scroller.scrollLeft + cardBounds.left - scrollerBounds.left - (scroller.clientWidth - cardBounds.width) / 2;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      scroller.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: reduceMotion ? 'auto' : scrollBehavior,
      });
    });
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.toggleAttribute('aria-busy', busy);
  }

  /**
   * @param {HTMLAnchorElement} link
   * @param {boolean} loading
   */
  #setLinkLoading(link, loading) {
    if (loading) {
      link.dataset.loadingBadgeTone = link.getAttribute('aria-current') === 'true' ? 'active' : 'default';
      link.setAttribute('aria-busy', 'true');
      return;
    }

    link.removeAttribute('aria-busy');
    delete link.dataset.loadingBadgeTone;
  }

  async #waitForLoadingIndicatorPaint() {
    await new Promise((resolve) => {
      window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
    });
  }

  #onCollectionUpdate = () => {
    this.#syncSelectedCard();
    this.#syncCategoryCounts();
  };

  #onPopState = () => {
    if (window.location.pathname !== this.dataset.collectionPath) return;

    this.#syncSelectedCard();
    const facetsForm = this.#getFacetsForm();

    if (facetsForm && 'updateFiltersByURL' in facetsForm && typeof facetsForm.updateFiltersByURL === 'function') {
      facetsForm.updateFiltersByURL(window.location.href, { updateHistory: false });
    }
  };

  #onPageShow = () => {
    this.#syncSelectedCard(undefined, 'auto');
  };

  #onLinkClick = async (event) => {
    if (!(event instanceof MouseEvent) || !(event.currentTarget instanceof HTMLAnchorElement)) return;

    const link = event.currentTarget;
    const releasePointerFocus = event.detail > 0;
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      link.target === '_blank' ||
      link.hasAttribute('download')
    ) {
      return;
    }

    if (this.#busy) {
      event.preventDefault();
      return;
    }

    const facetsForm = this.#getFacetsForm();
    if (!facetsForm || !('updateFiltersByURL' in facetsForm) || typeof facetsForm.updateFiltersByURL !== 'function') {
      return;
    }

    event.preventDefault();

    const targetUrl = new URL(link.href, window.location.href);
    targetUrl.searchParams.set(FORCED_AVAILABILITY_FILTER, FORCED_AVAILABILITY_VALUE);
    targetUrl.searchParams.delete('page');

    this.#setBusy(true);
    this.#setLinkLoading(link, true);

    try {
      await this.#waitForLoadingIndicatorPaint();
      this.#syncSelectedCard(targetUrl);
      await facetsForm.updateFiltersByURL(targetUrl.toString());
    } catch (error) {
      window.location.assign(targetUrl.toString());
    } finally {
      this.#setLinkLoading(link, false);
      this.#setBusy(false);
      if (releasePointerFocus && document.activeElement === link) link.blur();
    }
  };
}

if (!customElements.get('sale-category-tabs')) {
  customElements.define('sale-category-tabs', SaleCategoryTabs);
}
